import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, FAQAccordion, CTABand, Rise, Eyebrow, ArrowCard } from "@/components/chrome";
import { SERVICES } from "@/data/services";
import { findProvince, findCity, paths } from "@/lib/seo";
import { cityContent } from "@/lib/content";
import NotFound from "@/pages/NotFound";

export default function CityPage() {
  const { provinceSlug, citySlug } = useParams();
  const province = findProvince(provinceSlug);
  const city = province && findCity(provinceSlug, citySlug);
  if (!province || !city) return <NotFound />;
  const content = cityContent(city, province);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Locations", path: "/locations" },
    { name: province.name, path: paths.province(province.slug) },
    { name: city.name },
  ];

  return (
    <div data-testid="city-page">
      <PageHero prefix="city" crumbs={crumbs}
        eyebrow={`${province.name} — population ${city.pop}`}
        title={content.h1}
        lead={`12 professional cleaning services in ${city.name}, delivered by insured local crews. Flat-rate quotes by phone or email — usually within one business hour.`} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="city-overview">
        <div className="max-w-3xl space-y-5">
          {content.intro.map((p, i) => <p key={i} className="leading-relaxed text-slate-600 sm:text-lg">{p}</p>)}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="city-services">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>Services in {city.name}</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Every cleaning service {city.name} needs
            </h2></Rise>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <ArrowCard key={s.slug} to={paths.serviceCity(province.slug, city.slug, s.slug)}
                icon={s.icon} title={`${s.short} in ${city.name}`} desc={s.tagline}
                testid={`city-service-${s.slug}`} />
            ))}
          </div>
        </div>
      </section>

      {content.nearby.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="city-nearby">
          <Rise><Eyebrow>Nearby service areas</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Also serving communities near {city.name}
            </h2></Rise>
          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
            {content.nearby.map((c) => (
              <Link key={c.slug} to={paths.city(province.slug, c.slug)} data-testid={`city-nearby-${c.slug}`}
                className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
                Cleaning services in {c.name} <ArrowRight className="h-3 w-3 text-slate-300" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="border-t border-slate-200 bg-white" data-testid="city-faq-section">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {city.name} cleaning — common questions
            </h2></Rise>
          <div className="mt-8 max-w-3xl"><FAQAccordion faqs={content.faqs} testid="city-faq" /></div>
        </div>
      </section>

      <CTABand title={`Book a cleaning in ${city.name} today`}
        lead={`Call or email — a real coordinator will quote your ${city.name} cleaning, usually within one business hour.`}
        prefix="city-cta" />
    </div>
  );
}
